import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import EmojiPicker from "emoji-picker-react";
import {
  Send,
  Paperclip,
  Smile,
  Phone,
  Video,
  MoreVertical,
  ArrowLeft,
  Check,
  CheckCheck,
  Clock,
  AlertTriangle,
  MessageCircle,
  Wifi,
  WifiOff,
  User,
  Bot,
  UserCheck,
  Copy,
  ArrowRight,
  RefreshCw,
  Ban,
} from "lucide-react";
import { getChannelIcon, getRelativeTime } from "../../data/mockChatData.jsx";
import { useChatDetail } from "../../hooks/useChatDetail";
import MessageBubble from "./MessageBubble";
import ImagePreviewModal from "./ImagePreviewModal";
import ChatErrorBoundary from "./ChatErrorBoundary";

const ChatWindow = ({ selectedChatId, onBackToList, className = "" }) => {
  const [message, setMessage] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Navigation hooks for query params management
  const navigate = useNavigate();
  const location = useLocation();

  // Real-time chat detail hook
  const {
    messages,
    conversation,
    loading,
    error,
    sending,
    isConnected,
    sendMessage,
    assignConversation,
    resolveConversation,
    disableConversation,
    uploadImage,
    validateImageFile,
    refresh,
  } = useChatDetail(selectedChatId);

  // Image upload states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageCaption, setImageCaption] = useState("");

  // File input ref
  const fileInputRef = useRef(null);

  // File handling functions
  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate conversation status
    if (
      !conversation ||
      (conversation.status !== "assigned" && conversation.status !== "disable")
    ) {
      toast.error("Please assign conversation first to upload images");
      return;
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSelectedImage(file);
    setImagePreviewUrl(previewUrl);
    setImageCaption(""); // Reset caption for new image
    setShowImagePreview(true);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow same file selection
    e.target.value = "";
  };

  const handleAttachmentClick = () => {
    if (
      !conversation ||
      (conversation.status !== "assigned" && conversation.status !== "disable")
    ) {
      toast.error("Please assign conversation first to upload images");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImageSend = async (caption) => {
    if (!selectedImage) return;

    try {
      setIsUploadingImage(true);

      // Check if operator name exists
      const operatorName = localStorage.getItem("operatorName");
      if (!operatorName) {
        toast.error("Please set up your operator name first");
        return;
      }

      // Upload image
      const uploadResponse = await uploadImage(selectedImage);

      // Send message with image
      await sendMessage(caption, operatorName, uploadResponse.url);

      // Close preview and cleanup
      handleCloseImagePreview();
      toast.success("Image sent successfully!");
    } catch (err) {
      console.error("Error sending image:", err);
      toast.error(err.message || "Failed to send image");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleCloseImagePreview = () => {
    setShowImagePreview(false);
    setSelectedImage(null);
    setImageCaption(""); // Reset caption when closing
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
  };

  // 🔄 RESET INPUT & STATES when switching conversations
  useEffect(() => {
    // Reset all input-related states when conversation changes
    setMessage("");
    setShowEmojiPicker(false);

    // Close and cleanup image preview
    if (showImagePreview) {
      setShowImagePreview(false);
    }
    if (selectedImage) {
      setSelectedImage(null);
    }
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }

    console.log("🔄 Input state reset for conversation:", selectedChatId);
  }, [selectedChatId]); // Trigger when conversation ID changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Paste event handler for images
  useEffect(() => {
    const handlePaste = (e) => {
      // Only handle paste if chat input area is focused or if we're in the chat window
      const chatWindow = document.querySelector("[data-chat-window]");
      if (!chatWindow?.contains(e.target)) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find((item) => item.type.startsWith("image/"));

      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          handleFileSelect(file);
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [conversation, validateImageFile]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input when conversation changes
  useEffect(() => {
    if (selectedChatId && conversation && conversation.status === "assigned") {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [selectedChatId, conversation?.status]);

  // 😀 Emoji picker outside click & ESC key handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest("[data-emoji-button]")
      ) {
        setShowEmojiPicker(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showEmojiPicker) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      // Use both touchstart and click for better mobile support
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showEmojiPicker]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer?.files || []);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (imageFile) {
      handleFileSelect(imageFile);
    } else if (files.length > 0) {
      toast.error("Please drop image files only");
    }
  };

  const handleCopyPhone = async () => {
    try {
      const phoneNumber = conversation?.customer_phone || "123456789";
      await navigator.clipboard.writeText(phoneNumber);

      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error("❌ Failed to copy phone number:", err);

      // Fallback for older browsers
      try {
        const phoneNumber = conversation?.customer_phone || "123456789";
        const textArea = document.createElement("textarea");
        textArea.value = phoneNumber;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (fallbackErr) {
        console.error("❌ Fallback copy also failed:", fallbackErr);
        toast.error("Failed to copy phone number");
      }
    }
  };

  // 😀 Emoji picker functions
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiClick = (emojiObject) => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const emoji = emojiObject.emoji;

    // Insert emoji at cursor position
    const newMessage =
      message.substring(0, start) + emoji + message.substring(end);
    setMessage(newMessage);

    // Maintain cursor position after emoji (without auto-focus for mobile)
    setTimeout(() => {
      const newCursorPos = start + emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSendMessage = async () => {
    if (message.trim() && !sending) {
      try {
        // Check if operator name exists
        const operatorName = localStorage.getItem("operatorName");
        if (!operatorName) {
          toast.error("Please set up your operator name first");
          return;
        }

        await sendMessage(message, operatorName);
        setMessage("");
      } catch (err) {
        console.error("❌ Failed to send message:", err);
        toast.error(err.message || "Failed to send message. Please try again.");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 🎯 ACTION WRAPPERS: Update query params after actions
  const handleAssignWithNavigation = async () => {
    try {
      await assignConversation();
      // Navigate to assigned tab after successful assignment
      const newParams = new URLSearchParams(location.search);
      newParams.set("tab", "assigned");
      navigate(`/admin/chats/${selectedChatId}?${newParams.toString()}`);
      console.log("🔄 [Action] Navigated to assigned tab after assignment");
    } catch (error) {
      console.error("Failed to assign conversation:", error);
    }
  };

  const handleResolveWithNavigation = async () => {
    try {
      await resolveConversation();
      // Navigate to assigned tab without specific conversation (empty state)
      navigate("/admin/chats?tab=assigned");
      console.log("🔄 [Action] Navigated to assigned tab after resolution");
    } catch (error) {
      console.error("Failed to resolve conversation:", error);
    }
  };

  const handleDisableWithNavigation = async () => {
    try {
      await disableConversation();
      // Navigate to assigned tab without specific conversation (empty state)
      navigate("/admin/chats?tab=assigned");
      console.log("🔄 [Action] Navigated to assigned tab after disable");
    } catch (error) {
      console.error("Failed to disable conversation:", error);
    }
  };

  // Helper function to determine if "Take Over Conversation" button should be shown
  const shouldShowTakeOverButton = () => {
    if (!conversation) return false;

    // Condition 1: Status is "active"
    if (conversation.status === "active") {
      return true;
    }

    // Condition 2: Status is "escalated" AND assigned_to is null or empty
    if (
      conversation.status === "escalated" &&
      (!conversation.assigned_to || conversation.assigned_to === "")
    ) {
      return true;
    }

    return false;
  };

  if (!conversation) {
    return (
      <div
        className={`flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 ${className}`}
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
            Select a conversation
          </h3>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Choose a conversation from the sidebar to start chatting
          </p>
        </div>
      </div>
    );
  }

  return (
    <ChatErrorBoundary onRetry={refresh}>
      <div
        className={`flex-1 flex flex-col bg-white dark:bg-gray-800 ${className} relative`}
        data-chat-window
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Chat Header - Fixed on mobile for better UX */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 lg:relative lg:top-0 lg:z-auto mobile-header-fixed lg:mobile-header-fixed-reset">
          {/* Left: Customer Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Back button for mobile */}
            <button
              onClick={onBackToList}
              className="lg:hidden p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Customer Avatar */}
            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>

            {/* Customer Details */}
            <div className="min-w-0 flex-1">
              {/* Customer Name with Status Badge */}
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {conversation?.customer_name || "Loading..."}
                </h3>
                {/* Small Status Badge */}
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                    conversation?.status === "active"
                      ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                      : conversation?.status === "escalated"
                        ? "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300"
                        : conversation?.status === "assigned"
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                          : conversation?.status === "resolved"
                            ? "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300"
                            : conversation?.status === "disable"
                              ? "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300"
                              : "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300"
                  }`}
                >
                  {conversation?.status || "active"}
                  {conversation?.status === "assigned" &&
                    conversation.assigned_to && (
                      <span> by {conversation.assigned_to}</span>
                    )}
                </span>
              </div>

              {/* Phone Number with Copy Button */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="truncate">
                  {conversation?.customer_phone || "123456789"}
                </span>
                <button
                  onClick={handleCopyPhone}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                  title="Copy phone number"
                >
                  {isCopied ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* MOBILE: Dropdown Menu (lg:hidden) */}
            <div className="relative lg:hidden" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
              >
                <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                  {/* Show assign option when conversation needs to be taken over */}
                  {shouldShowTakeOverButton() && (
                    <button
                      onClick={() => {
                        handleAssignWithNavigation();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <UserCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      Assign
                    </button>
                  )}

                  {/* Show resolve option for assigned and disable status */}
                  {(conversation?.status === "assigned" ||
                    conversation?.status === "disable") && (
                    <button
                      onClick={() => {
                        handleResolveWithNavigation();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      Resolve Conversation
                    </button>
                  )}

                  {/* Show disable option for all statuses except disable itself */}
                  {conversation?.status !== "disable" && (
                    <button
                      onClick={() => {
                        handleDisableWithNavigation();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Ban className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      Disable Chat
                    </button>
                  )}

                  {/* Refresh option - always show */}
                  <button
                    onClick={() => {
                      refresh();
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    Refresh
                  </button>
                </div>
              )}
            </div>

            {/* DESKTOP: Individual Buttons (hidden lg:flex) */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Show assign button when conversation needs to be taken over */}
              {shouldShowTakeOverButton() && (
                <button
                  onClick={handleAssignWithNavigation}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg transition-colors"
                  title="Assign conversation to operator"
                >
                  <UserCheck className="w-4 h-4" />
                  <span>Assign</span>
                </button>
              )}

              {/* Show resolve button for assigned and disable status */}
              {(conversation?.status === "assigned" ||
                conversation?.status === "disable") && (
                <button
                  onClick={handleResolveWithNavigation}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg transition-colors"
                  title="Resolve conversation"
                >
                  <Check className="w-4 h-4" />
                  <span>Resolve</span>
                </button>
              )}

              {/* Show disable button for all statuses except disable itself */}
              {conversation?.status !== "disable" && (
                <button
                  onClick={handleDisableWithNavigation}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                  title="Disable chat responses"
                >
                  <Ban className="w-4 h-4" />
                  <span>Disable Chat</span>
                </button>
              )}

              {/* Refresh button - always show */}
              <button
                onClick={refresh}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                title="Refresh conversation"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* 🎯 Escalation Banner Section - Fixed on mobile, relative on desktop */}
        {conversation.escalation_reason && (
          <div className="px-4 py-3 lg:px-3 lg:py-2 bg-orange-100 dark:bg-orange-900/20 border-b border-gray-200 dark:border-gray-700 mobile-escalation-banner-fixed lg:mobile-escalation-banner-fixed-reset pl-safe pr-safe">
            <div className="w-full h-full flex items-center justify-center gap-2 text-sm text-orange-800 dark:text-orange-300">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="text-center mt-2 lg:mt-0">
                Escalated: {conversation.escalation_reason}
              </span>
            </div>
          </div>
        )}

        {/* 🎯 Resolved/Completed Banner Section - Fixed on mobile, relative on desktop */}
        {(conversation.status === "resolved" ||
          conversation.status === "completed") && (
          <div
            className={`px-4 py-3 lg:px-3 lg:py-2 bg-green-100 dark:bg-green-900/20 border-b border-gray-200 dark:border-gray-700 pl-safe pr-safe ${
              conversation.escalation_reason
                ? "mobile-resolved-banner-fixed lg:mobile-resolved-banner-fixed-reset"
                : "mobile-resolved-banner-fixed-no-escalation lg:mobile-resolved-banner-fixed-reset"
            }`}
          >
            <div className="w-full h-full flex items-center justify-center gap-2 text-sm text-green-800 dark:text-green-300">
              <Check className="w-4 h-4 flex-shrink-0" />
              <span className="text-center mt-2 lg:mt-0 capitalize">
                Conversation {conversation.status}
              </span>
            </div>
          </div>
        )}

        {/* Messages */}
        <div
          className={`flex-1 overflow-y-auto px-4 py-4 bg-gray-50 dark:bg-gray-900 mobile-content-with-input lg:mobile-content-reset lg:pt-4 ${
            // Dynamic padding based on banners present
            conversation.escalation_reason &&
            (conversation.status === "resolved" ||
              conversation.status === "completed")
              ? "mobile-content-with-header-and-both-banners"
              : conversation.escalation_reason
                ? "mobile-content-with-header-and-banner"
                : conversation.status === "resolved" ||
                    conversation.status === "completed"
                  ? "mobile-content-with-header-and-resolved"
                  : "mobile-content-with-header"
          }`}
        >
          {/* Messages area - Clean without status banners */}
          <div className="max-w-4xl mx-auto">
            {/* Loading state */}
            {loading && messages.length === 0 && (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="flex justify-center items-center h-32">
                <div className="text-center">
                  <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                  <button
                    onClick={refresh}
                    className="mt-2 px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            {!loading &&
              !error &&
              messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input - conditional based on conversation status */}
        {shouldShowTakeOverButton() ? (
          // Full-width assign button for conversations that need to be taken over
          <div className="px-2 py-4 lg:px-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pb-safe mobile-input-fixed lg:mobile-input-fixed-reset">
            <button
              onClick={() => handleAssignWithNavigation()}
              className="mb-4 w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-lg transition-all duration-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              <UserCheck className="w-5 h-5" />
              <span className="font-medium">Take Over Conversation</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        ) : (
          // Normal chat input for assigned conversations
          <div className="px-2 py-4 lg:px-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pb-safe mobile-input-fixed lg:mobile-input-fixed-reset">
            <div className="flex items-center gap-3">
              {/* Attachment button */}
              <button
                onClick={handleAttachmentClick}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex-shrink-0 flex items-center justify-center transition-colors mb-4"
                title="Upload image"
              >
                <Paperclip className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Message input */}
              <div className="flex-1 relative">
                <textarea
                  ref={messageInputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows="1"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
                  style={{ minHeight: "40px", maxHeight: "120px" }}
                />

                {/* Emoji button */}
                <button
                  onClick={toggleEmojiPicker}
                  data-emoji-button="true"
                  className={`absolute right-3 hover:bg-gray-100 dark:hover:bg-gray-600 rounded flex items-center justify-center transition-colors ${
                    showEmojiPicker ? "bg-gray-100 dark:bg-gray-600" : ""
                  }`}
                  title="Add emoji"
                  style={{ top: "40%", transform: "translateY(-52%)" }}
                >
                  <Smile
                    className={`w-5 h-5 transition-colors ${
                      showEmojiPicker
                        ? "text-green-500"
                        : "text-gray-600 dark:text-gray-400"
                    }`}
                  />
                </button>
              </div>

              {/* Send button */}
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || sending}
                className={`p-2 rounded-full flex-shrink-0 transition-colors flex items-center justify-center ${
                  message.trim() && !sending
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                }`}
              >
                {sending ? (
                  <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* 😀 Emoji Picker - Responsive positioning */}
        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className="fixed z-50 lg:absolute lg:bottom-16 lg:right-4"
            style={{
              // Mobile: full width bottom sheet
              bottom: window.innerWidth <= 768 ? "0" : "auto",
              left: window.innerWidth <= 768 ? "0" : "auto",
              right: window.innerWidth <= 768 ? "0" : "auto",
              // Desktop: positioned relative to input
              ...(window.innerWidth > 768 && {
                bottom: "80px",
                right: "16px",
              }),
            }}
          >
            {/* Mobile backdrop */}
            {typeof window !== "undefined" && window.innerWidth <= 768 && (
              <div
                className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm z-40"
                onClick={() => setShowEmojiPicker(false)}
                onTouchStart={() => setShowEmojiPicker(false)}
              />
            )}

            <div
              className={`
              ${
                window.innerWidth <= 768
                  ? "w-full h-80 rounded-t-2xl"
                  : "w-80 h-96 rounded-xl shadow-xl"
              }
              bg-white dark:bg-gray-800 
              border border-gray-200 dark:border-gray-600
              overflow-hidden
              transform transition-all duration-300 ease-out
              relative z-50
              ${
                showEmojiPicker
                  ? "translate-y-0 opacity-100"
                  : "translate-y-full opacity-0"
              }
            `}
            >
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                theme={
                  document.documentElement.classList.contains("dark")
                    ? "dark"
                    : "light"
                }
                width="100%"
                height="100%"
                searchDisabled={window.innerWidth <= 768}
                autoFocusSearch={window.innerWidth > 768}
                skinTonesDisabled={false}
                previewConfig={{ showPreview: false }}
                categories={[
                  "suggested",
                  "smileys_people",
                  "animals_nature",
                  "food_drink",
                  "activities",
                  "travel_places",
                  "objects",
                  "symbols",
                  "flags",
                ]}
                style={{
                  "--epr-emoji-size": "20px",
                  "--epr-search-input-height":
                    window.innerWidth <= 768 ? "0px" : "40px",
                }}
              />
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        <ImagePreviewModal
          isOpen={showImagePreview}
          onClose={handleCloseImagePreview}
          imageFile={selectedImage}
          imagePreviewUrl={imagePreviewUrl}
          onSend={handleImageSend}
          isUploading={isUploadingImage}
          caption={imageCaption}
          onCaptionChange={setImageCaption}
        />
      </div>
    </ChatErrorBoundary>
  );
};

export default ChatWindow;
